import React, {useEffect, useMemo, useRef, useState} from 'react'
import styles from './UIGameLayout.module.scss'
import {UIGameEnd} from '../UIGameEnd'
import {UIGameFeeds} from '../UIGameFeeds/UIGameFeeds'
import {UIGamePause} from '../UIGamePause/UIGamePause'
import {UIGameUserSettings} from '../UIGameUserSettings'
import {UIGameHelpButtons} from '../UIGameHelpButtons/UIGameHelpButtons'
import {StorageWrapper} from '../../utils/storage'
import {UIGameSpectators} from '../UIGameSpectators/UIGameSpectators'
import {UIGameCardAnimator} from '../UIGameCardAnimator/UIGameCardAnimator'
import {UIGameVoteContainer} from '../UIGameVote/UIGameVoteContainer'
import {UIGameEndReturnToStats} from '../UIGameEnd/UIGameEndReturnToStats/UIGameEndReturnToStats'
import {UIGameTradeOffers} from '../UIGameTradeOffer/UIGameTradeOffers'
import {UIGameAchievementContainer} from '../UIGameAchievement/UIGameAchievementContainer'
import {ModeSettingEnum} from '@colonist/types'
import {UIGameFeedsPortraitVisibility} from '../UIGameFeeds/UIGameFeedsPortrait'
import {UIGameBank} from '../UIGameBank'
import {UIGamePlayerInformationContainer} from '../UIGamePlayerInformation/UIGamePlayerInformationContainer'
import {useGameStore} from '../../store/GameStoreContext'
import {UIGameTimerBarContainer} from '../UIGameTimerBar/UIGameTimerBarContainer'
import {UIGameBarbarianInvasionTrackerContainer} from '../UIGameBarbarianInvasionTracker/UIGameBarbarianInvasionTrackerContainer'
import {UIGameReplayControlsContainer} from '../UIGameReplayControls/UIGameReplayControlsContainer'
import {useIsReplay} from '../../store/selectors/useIsReplay'
import clsx from 'clsx'
import {UIGameBeginnerHint} from '../UIGameBeginnerHint'
import {UIGameBottomLandscape, UIGameBottomPortrait} from '../UIGameBottom'
import {UIGamePortraitTop} from '../UIGamePortraitTop'
import {useUIGameLandscapeResponsive} from './useUIGameResponsive'
import {useOrientation} from '../../hooks/useOrientation'
import {UIGameRotateDeviceDialog} from '../UIGameRotateDeviceDialog/UIGameRotateDeviceDialog'
import {useUIContext} from '../../contexts/UIContext'
import {UIGamePlayerInfoContainerAll} from '../UIGamePlayerInfoContainerAll/UIGamePlayerInfoContainerAll'
import {PlayerInfoSection} from '../UIGamePlayerInfoContainer/UIGamePlayerInfoContainer'

type UIGameOrientation = 'portrait' | 'landscape'

interface UIGameLayoutProps {
    orientation: UIGameOrientation
    isChatDefaultMinimized: boolean
    isChatEnabled: boolean
    onSendChat: (message: string) => void
    initialCanResign: boolean
}

export const UIGameLayout = (props: UIGameLayoutProps) => {
    if(props.orientation === 'portrait') return <UIGameLayoutPortrait {...props} />
    else return <UIGameLayoutLandscape {...props} />
}

export const UIGameLayoutLandscape = ({
    isChatDefaultMinimized,
    isChatEnabled,
    onSendChat,
    initialCanResign,
}: UIGameLayoutProps) => {
    const onClickLogForReplay = useGameStore((state) => state.gameReplay.replayControls?.onClickLog)
    const onClickLog = onClickLogForReplay ?? (() => void 0)
    const isReplay = useIsReplay()
    const containerRef = useRef<HTMLDivElement>(null)
    const containerFontSize = useUIGameLandscapeResponsive(containerRef)

    return (
        <div
            ref={containerRef}
            className={clsx(styles.containerLandscape)}
            style={{
                '--container-font-size': `${containerFontSize}rem`,
                gridTemplateColumns: `
                                    [start]
                                    auto
                                    [help-buttons-end] 
                                    1fr
                                    [game-feeds]
                                    19em
                                    [end]`,
                gridTemplateRows: `
                                    [start]
                                    1fr
                                    [game-feeds-end]
                                    auto
                                    [bank-end]
                                    21.25em
                                    [end]`,
            } as React.CSSProperties}>
            <div className={styles.gameTopLeftContainer}>
                <div className={styles.gameHelpButtonsContainer}>
                    <UIGameHelpButtons className={styles.gameHelpButtonsLayer} initialCanResign={initialCanResign} />
                </div>
                <div className={styles.gameSpectatorsContainer}>
                    <UIGameSpectators />
                </div>
                <div className={styles.gameBarbarianInvasionTrackerContainer}>
                    <UIGameBarbarianInvasionTrackerContainer />
                </div>
            </div>
            <div className={styles.gameSettingsContainer}>
                <UIGameUserSettings />
            </div>
            <div className={styles.gameEndContainer}>
                <UIGameEnd />
            </div>
            <div className={styles.gamePauseContainer}>
                <UIGamePause />
            </div>
            <div className={styles.gameTradeOffersContainer}>
                <UIGameTradeOffers className={styles.gameTradeOffersWrapper}/>
            </div>
            <div className={styles.gameBottomContainer}>
                <UIGameBottomLandscape />
            </div>
            <div className={styles.gameAchievementContainer}>
                <UIGameAchievementContainer />
            </div>
            <UIGameFeeds
                className={styles.gameFeedsContainer}
                isChatDefaultMinimized={isChatDefaultMinimized}
                isChatEnabled={isChatEnabled}
                onClickLog={onClickLog}
                onSendChat={onSendChat}/>
            <UIGameBank
                className={styles.gameBankContainer}
                isLandscape={true}
            />
            <UIGamePlayerInformationContainer className={styles.gamePlayerInformationContainer} />
            <div className={styles.gameReplayControlsContainer}>
                {isReplay === true && <div className={styles.gameReplayControlsContainerInner}>
                    <UIGameReplayControlsContainer useMinimalControls={false} />
                </div>}
                <UIGameEndReturnToStats text="strings:game.endGame.cornerButton.openStats" />
            </div>
            <div className={styles.gameCardAnimatorContainer}>
                <UIGameCardAnimator />
            </div>
            <UIGameBeginnerHint/>
        </div>
    )
}

export const UIGameLayoutPortrait = ({
    onSendChat,
    initialCanResign,
}: UIGameLayoutProps) => {
    const safeAreaTop = StorageWrapper.getStatusBarHeight() ?? 0
    const safeAreaBottom = (StorageWrapper.getSafeAreaInsetBottom() ?? 0) * 0.5
    const isReplay = useIsReplay()
    const modeSetting = useGameStore((state) => state.gameSettings.modeSetting)
    const isBeginnerMode = modeSetting === ModeSettingEnum.BeginnerModeClassic4P
    const [feedsPortraitVisibility, setFeedsPortraitVisibility] = useState<UIGameFeedsPortraitVisibility>('none')
    const onClickLogForReplay = useGameStore((state) => state.gameReplay.replayControls?.onClickLog)
    const onClickLog = (index: number) => onClickLogForReplay?.(index)

    const [isPlayerInfoSectionUsedDevsActive, setIsPlayerInfoSectionUsedDevsActive] =
        useState(StorageWrapper.getPlayerInfoSectionUsedDevsActive() ?? false)
    const [isPlayerInfoSectionCKActive, setIsPlayerInfoSectionCKActive] = useState(StorageWrapper.getPlayerInfoSectionCKActive() ?? false)
    const [isPlayerInfoSectionPiecesActive, setIsPlayerInfoSectionPiecesActive] = useState(StorageWrapper.getPlayerInfoSectionPiecesActive() ?? false)

    useEffect(() => {
        StorageWrapper.setPlayerInfoSectionUsedDevsActive(isPlayerInfoSectionUsedDevsActive)
    }, [isPlayerInfoSectionUsedDevsActive])

    useEffect(() => {
        StorageWrapper.setPlayerInfoSectionCKActive(isPlayerInfoSectionCKActive)
    }, [isPlayerInfoSectionCKActive])

    useEffect(() => {
        StorageWrapper.setPlayerInfoSectionPiecesActive(isPlayerInfoSectionPiecesActive)
    }, [isPlayerInfoSectionPiecesActive])

    const visiblePlayerInfoSections = useMemo((): PlayerInfoSection[] => {
        if(isBeginnerMode) return ['hand']
        return (['hand', 'pieces', 'usedDevs', 'ck'] as const).filter((section) => {
            if(section === 'usedDevs') return isPlayerInfoSectionUsedDevsActive
            if(section === 'ck') return isPlayerInfoSectionCKActive
            if(section === 'pieces') return isPlayerInfoSectionPiecesActive
            return true
        })
    }, [isBeginnerMode, isPlayerInfoSectionUsedDevsActive, isPlayerInfoSectionCKActive, isPlayerInfoSectionPiecesActive])

    const orientation = useOrientation()
    const isMobileWebBrowser = useUIContext().isUserOnMobileWebBrowser
    const isTablet = useUIContext().isUserOnTablet
    if(orientation === 'landscape' && isMobileWebBrowser && !isTablet) {
        return <UIGameRotateDeviceDialog />
    }

    return (
        <div
            className={styles.containerPortrait}
            style={{
                gridTemplateColumns: 'minmax(0, 1fr)',
                gridTemplateRows: `
                                    [start]
                                    ${safeAreaTop}px
                                    [safe-area-top-end]
                                    min-content
                                    [help-buttons-end]
                                    min-content
                                    [player-information-container-end]
                                    minmax(min(20vh, 80px), 1fr)
                                    [bottom-start]
                                    auto
                                    [timer-bar-start]
                                    auto
                                    [safe-area-bottom-start]
                                    ${safeAreaBottom}px
                                    [end]`,
            }}>
            <div className={styles.gameHelpBackground}></div>
            <UIGamePortraitTop
                initialCanResign={initialCanResign}
                className={styles.gameTopContainer}
                isPlayerInfoSectionUsedDevsActive={isPlayerInfoSectionUsedDevsActive}
                setIsPlayerInfoSectionUsedDevsActive={setIsPlayerInfoSectionUsedDevsActive}
                isPlayerInfoSectionCKActive={isPlayerInfoSectionCKActive}
                setIsPlayerInfoSectionCKActive={setIsPlayerInfoSectionCKActive}
                isPlayerInfoSectionPiecesActive={isPlayerInfoSectionPiecesActive}
                setIsPlayerInfoSectionPiecesActive={setIsPlayerInfoSectionPiecesActive} />
            <div className={styles.gamePlayerInfoContainer}>
                <UIGamePlayerInfoContainerAll visibleSections={visiblePlayerInfoSections} />
            </div>
            {isReplay === false && <div className={styles.gameTradeOffersContainer}>
                <UIGameTradeOffers className={styles.gameTradeOffersWrapper} disableTooltips/>
            </div>}
            {isReplay === false && isBeginnerMode === false && <div className={styles.gameTimerBarContainer}>
                <UIGameTimerBarContainer />
            </div>}
            <div className={styles.gameSettingsContainer}>
                <UIGameUserSettings
                    enabledTabs={['game-info', 'display', 'audio']}
                    isNestedContentEnabled={true} />
            </div>
            <div className={styles.gameEndContainer}>
                <UIGameEnd />
            </div>
            <div className={styles.gamePauseContainer}>
                <UIGamePause />
            </div>
            <div className={styles.gameAchievementContainer}>
                <UIGameAchievementContainer />
            </div>
            <div className={styles.gameBarbarianInvasionTrackerContainer}>
                <UIGameBarbarianInvasionTrackerContainer />
            </div>
            <div className={styles.gameCardAnimatorContainer}>
                <UIGameCardAnimator />
            </div>
            <div className={styles.gameVoteContainer}>
                <UIGameVoteContainer />
            </div>
            <div className={styles.gameBottomContainer}>
                <UIGameBottomPortrait
                    onClickLog={onClickLog}
                    onSendChat={onSendChat}
                    feedsPortraitVisibility={feedsPortraitVisibility}
                    setFeedsPortraitVisibility={setFeedsPortraitVisibility} />
            </div>
            <UIGameBeginnerHint />
        </div>
    )
}
